import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { BASE_URL } from '../../../services/configUrls';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Grid,
  useTheme,
  useMediaQuery,
  TextField,
  Stack,
  CircularProgress,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Popover,
  Tooltip
} from '@mui/material';
import {
  ThumbUp,
  CalendarToday,
  Schedule,
  Person,
  Business,
  Link as LinkIcon,
  CameraAlt,
  Download,
  Videocam,
  VideocamOff,
  Info,
  Delete,
  Warning,
  CalendarMonth,
  Image as ImageIcon,
  RemoveRedEye,
  PictureAsPdf,
  ZoomIn
} from '@mui/icons-material';
import SpocCertificateDownload from './SpocCertificateDownload';


import careerTalkPosterImage from '../../../assets/career_talk_poster.jpg';
const CareerTalk_Spoc = () => {
  // Get access token from Redux state
  const accessToken = useSelector((state) => state.auth.accessToken);

  const [activeTab, setActiveTab] = useState(0);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [showUploadPhotosDialog, setShowUploadPhotosDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showPhotoAlertDialog, setShowPhotoAlertDialog] = useState(false);
  const [showPosterDialog, setShowPosterDialog] = useState(false);
  const [selectedTalk, setSelectedTalk] = useState(null);
  const [studentCount, setStudentCount] = useState('');
  const [capturedImages, setCapturedImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  // Add this new state for social media links
  const [socialMediaLinks, setSocialMediaLinks] = useState([""]);

  const [loading, setLoading] = useState({
    upcoming: false,
    participating: false,
    participated: false,
    completed: false,
    enroll: false,
    complete: false,
    viewPhotos: false,
    posterLoading: {} // Track loading state for individual posters
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);
  const [showCertificateDialog, setShowCertificateDialog] = useState(false);
  const [posterCache, setPosterCache] = useState({}); // Cache fetched poster URLs

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  // Pagination states
  const [pagination, setPagination] = useState({
    page: 1,
    rowsPerPage: 10,
    totalCount: 0
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Use dynamic auth token with Bearer prefix
  const AUTH_TOKEN = accessToken ? `Bearer ${accessToken}` : '';

  const [careerTalks, setCareerTalks] = useState({
    upcoming: [],
    participating: [],
    participated: [],
    completed: []
  });

  // Check if user is authenticated
  useEffect(() => {
    if (!accessToken) {
      showSnackbar('Authentication required. Please log in.', 'error');
    }
  }, [accessToken]);

  // Generate month and year options
  const currentYear = new Date().getFullYear();
  const monthOptions = [
    { value: '', label: 'All Months' },
    { value: '0', label: 'January' },
    { value: '1', label: 'February' },
    { value: '2', label: 'March' },
    { value: '3', label: 'April' },
    { value: '4', label: 'May' },
    { value: '5', label: 'June' },
    { value: '6', label: 'July' },
    { value: '7', label: 'August' },
    { value: '8', label: 'September' },
    { value: '9', label: 'October' },
    { value: '10', label: 'November' },
    { value: '11', label: 'December' }
  ];

  const yearOptions = [
    { value: '', label: 'All Years' },
    { value: (currentYear - 1).toString(), label: (currentYear - 1).toString() },
    { value: currentYear.toString(), label: currentYear.toString() },
    { value: (currentYear + 1).toString(), label: (currentYear + 1).toString() }
  ];

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const setLoadingState = (key, value) => {
    setLoading(prev => ({ ...prev, [key]: value }));
  };

  const setPosterLoading = (talkId, value) => {
    setLoading(prev => ({
      ...prev,
      posterLoading: {
        ...prev.posterLoading,
        [talkId]: value
      }
    }));
  };

  // Check if event link should be visible (only on event morning)
  const isEventLinkVisible = (talk) => {
    const now = new Date();
    const eventDate = new Date(talk.date);

    // Check if it's the same day and it's morning (before 12 PM)
    const isSameDay = now.toDateString() === eventDate.toDateString();
    const isMorning = now.getHours() < 12;

    return isSameDay && isMorning && talk.spocStatus === 'accepted';
  };

  // Filter talks by month and year
  const filterTalksByDate = (talks) => {
    return talks.filter(talk => {
      const eventDate = new Date(talk.date);
      const eventMonth = eventDate.getMonth().toString();
      const eventYear = eventDate.getFullYear().toString();

      const monthMatch = !selectedMonth || eventMonth === selectedMonth;
      const yearMatch = !selectedYear || eventYear === selectedYear;

      return monthMatch && yearMatch;
    });
  };

  // Paginate talks
  const getPaginatedTalks = (talks) => {
    const startIndex = (pagination.page - 1) * pagination.rowsPerPage;
    const endIndex = startIndex + pagination.rowsPerPage;
    return talks.slice(startIndex, endIndex);
  };

  // Handle page change
  const handlePageChange = (event, value) => {
    setPagination(prev => ({
      ...prev,
      page: value
    }));

    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Reset pagination when filters or tabs change
  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      page: 1
    }));
  }, [activeTab, selectedMonth, selectedYear]);

  // Update total count when filtered talks change
  useEffect(() => {
    const filteredTalks = filterTalksByDate(getTalksForCurrentTab());
    setPagination(prev => ({
      ...prev,
      totalCount: filteredTalks.length
    }));
  }, [careerTalks, activeTab, selectedMonth, selectedYear]);

  // Calendar handlers
  const handleCalendarOpen = (event) => {
    setCalendarAnchorEl(event.currentTarget);
  };

  const handleCalendarClose = () => {
    setCalendarAnchorEl(null);
  };

  const clearDateFilter = () => {
    setSelectedMonth('');
    setSelectedYear('');
  };

  const calendarOpen = Boolean(calendarAnchorEl);

  // Function to fetch images from S3
  const fetchImageFromS3 = async (fileKey) => {
    try {
      if (!fileKey || !AUTH_TOKEN) return null;

      const response = await fetch(`${BASE_URL}/get-image-from-s3`, {
        method: 'POST',
        headers: {
          'Authorization': AUTH_TOKEN,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ file_key: fileKey }),
      });

      if (!response.ok) {
        console.error(`Failed to fetch image for key: ${fileKey}`);
        return null;
      }

      const data = await response.json();
      return data.url || null;
    } catch (error) {
      console.error('Error fetching image from S3:', error);
      return null;
    }
  };

  const transformTalk = async (talk, category) => {
    const enrollment = talk.spoc_enrollment || {};

    console.log('Transforming talk:', talk.id, 'Enrollment:', enrollment);

    // Handle speaker array - convert to display string
    let speakerDisplay = 'Speaker Name';
    let speakerNames = [];
    let companyDisplay = 'Company Name';

    if (talk.speaker && Array.isArray(talk.speaker) && talk.speaker.length > 0) {
      // Extract speaker names from array
      speakerNames = talk.speaker.map(s => s.speaker_name).filter(Boolean);
      speakerDisplay = speakerNames.join(', ');

      // Get company from first speaker
      if (talk.speaker[0].company) {
        companyDisplay = talk.speaker[0].company;
      }
    } else if (typeof talk.speaker === 'string') {
      speakerDisplay = talk.speaker;
      companyDisplay = talk.company || companyDisplay;
    }

    // Handle designation
    let designationDisplay = 'Speaker Designation';
    if (talk.speaker && Array.isArray(talk.speaker) && talk.speaker.length > 0 && talk.speaker[0].designation) {
      designationDisplay = talk.speaker[0].designation;
    } else if (talk.designation) {
      designationDisplay = talk.designation;
    }

    // Store original poster key - we'll fetch it on demand when user clicks to view
    return {
      id: talk.id,
      enrollmentId: enrollment.enrollment_id || null,
      title: talk.topic,
      speaker: speakerDisplay,
      speakerArray: talk.speaker || [],
      designation: designationDisplay,
      company: companyDisplay,
      date: talk.date,
      startTime: talk.start_time,
      endTime: talk.end_time,
      link: talk.link,
      posterKey: talk.poster_url, // Store the key for later fetching
      posterUrl: null, // Will be fetched when user clicks to view
      status: getStatusFromApi(talk.event_status || category),
      spocStatus: getSpocStatusFromApi(enrollment, talk.spoc_status),
      description: `${talk.topic} - Career talk session by ${speakerDisplay} from ${companyDisplay}.`,
      certificateUrl: enrollment.certificate?.certificate_file_url || null,
      termsAccepted: enrollment.terms_accepted === "1",
      staffApproved: enrollment.is_staff_approval === "1",
      staffUserId: enrollment.staff_user_id,
      studentsAttended: enrollment.students_attended,
      eventPhotoUrl: enrollment.event_photo_url,
      eventStartTime: talk.start_time,
      eventDate: talk.date,
      hasParticipated: enrollment.terms_accepted === "1",
      showInterestedButton: talk.show_interested_button === true,
      totalInterested: talk.total_interested,
      isCertificateIssued: enrollment.is_issue === "1",
      spoc_enrollment: enrollment
    };
  };

  const fetchUpcomingTalks = async () => {
    try {
      if (!AUTH_TOKEN) {
        showSnackbar('Authentication required', 'error');
        return;
      }

      setLoadingState('upcoming', true);
      const response = await fetch(`${BASE_URL}/new_support/spoc/new_upcoming`, {
        method: 'GET',
        headers: {
          'Authorization': AUTH_TOKEN,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || errorData.message || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Upcoming API Response:', data);

      const transformedPromises = (data.upcoming || []).map(talk => transformTalk(talk, 'upcoming'));
      const transformedData = await Promise.all(transformedPromises);

      setCareerTalks(prev => ({ ...prev, upcoming: transformedData }));

    } catch (error) {
      console.error('Error fetching upcoming talks:', error);
      const errorMessage = typeof error === 'object' && error.message ? error.message : 'Failed to load upcoming talks';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoadingState('upcoming', false);
    }
  };

  const fetchParticipatingTalks = async () => {
    try {
      if (!AUTH_TOKEN) {
        showSnackbar('Authentication required', 'error');
        return;
      }

      setLoadingState('participating', true);
      const response = await fetch(`${BASE_URL}/new_support/spoc/participating_2`, {
        method: 'GET',
        headers: {
          'Authorization': AUTH_TOKEN,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || errorData.message || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Participating API Response:', data);

      const transformedPromises = (data.participating || []).map(talk => transformTalk(talk, 'participating'));
      const transformedData = await Promise.all(transformedPromises);

      setCareerTalks(prev => ({ ...prev, participating: transformedData }));

    } catch (error) {
      console.error('Error fetching participating talks:', error);
      const errorMessage = typeof error === 'object' && error.message ? error.message : 'Failed to load participating talks';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoadingState('participating', false);
    }
  };

  const fetchParticipatedTalks = async () => {
    try {
      if (!AUTH_TOKEN) {
        showSnackbar('Authentication required', 'error');
        return;
      }

      setLoadingState('participated', true);
      const response = await fetch(`${BASE_URL}/new_support/spoc/participated_2`, {
        method: 'GET',
        headers: {
          'Authorization': AUTH_TOKEN,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || errorData.message || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Participated API Response:', data);

      const transformedPromises = (data.completed || []).map(talk => transformTalk(talk, 'participated'));
      const transformedData = await Promise.all(transformedPromises);

      setCareerTalks(prev => ({ ...prev, participated: transformedData }));

    } catch (error) {
      console.error('Error fetching participated talks:', error);
      const errorMessage = typeof error === 'object' && error.message ? error.message : 'Failed to load participated talks';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoadingState('participated', false);
    }
  };

  const fetchCompletedTalks = async () => {
    try {
      if (!AUTH_TOKEN) {
        showSnackbar('Authentication required', 'error');
        return;
      }

      setLoadingState('completed', true);
      const response = await fetch(`${BASE_URL}/new_support/spoc/completed_2?page=1&limit=20`, {
        method: 'GET',
        headers: {
          'Authorization': AUTH_TOKEN,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || errorData.message || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Completed API Response:', data);

      const transformedPromises = (data.completed || []).map(talk => transformTalk(talk, 'completed'));
      const transformedData = await Promise.all(transformedPromises);

      setCareerTalks(prev => ({ ...prev, completed: transformedData }));

    } catch (error) {
      console.error('Error fetching completed talks:', error);
      const errorMessage = typeof error === 'object' && error.message ? error.message : 'Failed to load completed talks';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoadingState('completed', false);
    }
  };

  const fetchAllTalks = async () => {
    await Promise.all([
      fetchUpcomingTalks(),
      fetchParticipatingTalks(),
      fetchParticipatedTalks(),
      fetchCompletedTalks()
    ]);
  };

  const acceptTermsAndConditions = async (careerTalkId) => {
    try {
      if (!AUTH_TOKEN) {
        showSnackbar('Authentication required', 'error');
        return;
      }

      setLoadingState('enroll', true);

      const formData = new FormData();
      formData.append('career_talk_id', careerTalkId.toString());

      const response = await fetch(`${BASE_URL}/new_support/spoc/enroll`, {
        method: 'POST',
        headers: {
          'Authorization': AUTH_TOKEN,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || errorData.message || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      const data = await response.json();
      showSnackbar('Successfully enrolled for the career talk!');

      // Refresh all data after enrollment
      await fetchAllTalks();

      return data;
    } catch (error) {
      console.error('Error accepting terms:', error);
      const errorMessage = typeof error === 'object' && error.message ? error.message : 'Failed to enroll for career talk';
      showSnackbar(errorMessage, 'error');
      throw error;
    } finally {
      setLoadingState('enroll', false);
    }
  };

  const submitEventPhotos = async (enrollment_id, studentsAttended, eventPhotos, socialLinks) => {
    try {
      if (!AUTH_TOKEN) {
        showSnackbar('Authentication required', 'error');
        return;
      }

      setLoadingState('complete', true);

      const formData = new FormData();
      formData.append('students_attended', studentsAttended.toString());

      eventPhotos.forEach((photo, index) => {
        formData.append('event_photos', photo);
      });
      const validLinks = socialLinks.filter(link => link.trim() !== "");
      if (validLinks.length > 0) {
        // Send as JSON string
        formData.append('social_media_links', JSON.stringify(validLinks));
      }

      const response = await fetch(`${BASE_URL}/new_support/spoc/complete_2/${enrollment_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': AUTH_TOKEN,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || errorData.message || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Photo upload response:', data);
      showSnackbar(`Successfully submitted ${eventPhotos.length} photo(s)!`);

      // Refresh participating and participated talks after photo upload
      await Promise.all([
        fetchParticipatingTalks(),
        fetchParticipatedTalks()
      ]);

      return data;
    } catch (error) {
      console.error('Error submitting photos:', error);
      const errorMessage = typeof error === 'object' && error.message ? error.message : 'Failed to submit event photos';
      showSnackbar(errorMessage, 'error');
      throw error;
    } finally {
      setLoadingState('complete', false);
    }
  };

  const isValidUrl = (urlString) => {
    try {
      const url = new URL(urlString);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch (_) {
      return false;
    }
  };

  const handleSubmitPhotos = async () => {
    if (selectedTalk && studentCount && selectedImages.length > 0) {
      // Validate social media links (if any are provided)
      const validLinks = socialMediaLinks.filter(link => link.trim() !== "");
      const invalidLinks = validLinks.filter(link => !isValidUrl(link));

      if (invalidLinks.length > 0) {
        showSnackbar(`Invalid URL(s) found: ${invalidLinks.join(", ")}`, "error");
        return;
      }
      try {
        await submitEventPhotos(selectedTalk.enrollmentId, studentCount, selectedImages, socialMediaLinks.filter(link => link.trim() !== ""));
        setShowUploadPhotosDialog(false);
        setSelectedTalk(null);
        setStudentCount('');
        setSocialMediaLinks([""]);
        capturedImages.forEach(url => URL.revokeObjectURL(url));
        setCapturedImages([]);
        setSelectedImages([]);
      } catch (error) {
        // Error handled in the function
      }
    } else {
      showSnackbar('Please fill all fields and capture/select at least one photo', 'warning');
    }
  };

  // Fetch poster image when user clicks to view
  const handleFetchAndShowPoster = async (talk) => {
    if (!talk.posterKey) {
      showSnackbar('No poster available for this event', 'info');
      return;
    }

    // Check cache first
    if (posterCache[talk.posterKey]) {
      const cachedTalk = {
        ...talk,
        posterUrl: posterCache[talk.posterKey]
      };
      setSelectedTalk(cachedTalk);
      setShowPosterDialog(true);
      return;
    }

    setPosterLoading(talk.id, true);
    try {
      // Fetch poster from S3
      const posterUrl = await fetchImageFromS3(talk.posterKey);

      if (posterUrl) {
        // Cache the fetched URL
        setPosterCache(prev => ({
          ...prev,
          [talk.posterKey]: posterUrl
        }));

        // Update the talk with fetched poster URL
        const updatedTalk = {
          ...talk,
          posterUrl: posterUrl
        };

        setSelectedTalk(updatedTalk);
        setShowPosterDialog(true);
      } else {
        showSnackbar('Unable to load poster image', 'error');
      }
    } catch (error) {
      console.error('Error fetching poster:', error);
      showSnackbar('Failed to load poster', 'error');
    } finally {
      setPosterLoading(talk.id, false);
    }
  };

  // Camera functions
  const initializeCamera = async () => {
    setCameraLoading(true);
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      if (!videoRef.current) {
        throw new Error('Video element not found');
      }

      const constraints = {
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      const video = videoRef.current;
      video.srcObject = stream;

      await new Promise((resolve) => {
        video.onloadedmetadata = () => {
          video.play().then(resolve).catch(console.error);
        };
      });

      setIsCameraActive(true);
      setCameraLoading(false);
    } catch (error) {
      console.error('Error initializing camera:', error);
      stopCamera();
      setCameraLoading(false);

      let errorMessage = 'Failed to start camera. ';
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Camera permission denied. Please allow camera access.';
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'No camera found on this device.';
      } else if (error.name === 'NotReadableError') {
        errorMessage = 'Camera is already in use by another application.';
      } else {
        errorMessage += error.message || 'Unknown error.';
      }

      showSnackbar(errorMessage, 'error');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraActive(false);
    setCameraLoading(false);
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas) {
      showSnackbar("Camera not ready. Please try again.", "warning");
      return;
    }

    if (!video.videoWidth || !video.videoHeight) {
      showSnackbar("Camera stream not ready. Please wait a moment.", "warning");
      return;
    }

    try {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            showSnackbar("Failed to capture image", "error");
            return;
          }

          const file = new File([blob], `event-photo-${Date.now()}.jpg`, {
            type: "image/jpeg",
          });

          const url = URL.createObjectURL(file);

          setSelectedImages((prev) => [...prev, file]);
          setCapturedImages((prev) => [...prev, url]);

          showSnackbar(`Photo captured! (${selectedImages.length + 1}/3)`, "success");
        },
        "image/jpeg",
        0.8
      );
    } catch (error) {
      console.error('Error capturing photo:', error);
      showSnackbar("Error capturing photo. Please try again.", "error");
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    if (imageFiles.length === 0) {
      showSnackbar('Please select image files', 'error');
      return;
    }

    setSelectedImages(prevSelected => {
      const allowed = Math.max(0, 4 - prevSelected.length);
      if (allowed === 0) {
        showSnackbar('Maximum 3 photos already selected.', 'warning');
        return prevSelected;
      }

      const toAdd = imageFiles.slice(0, allowed);
      const newSelected = [...prevSelected, ...toAdd];

      const newUrls = toAdd.map(f => URL.createObjectURL(f));
      setCapturedImages(prevUrls => [...prevUrls, ...newUrls]);

      showSnackbar(`${toAdd.length} photo(s) added! (${newSelected.length}/3)`, 'success');
      return newSelected;
    });

    event.target.value = '';
  };

  const removePhoto = (index) => {
    URL.revokeObjectURL(capturedImages[index]);

    setCapturedImages(prev => prev.filter((_, i) => i !== index));
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    showSnackbar('Photo removed', 'info');
  };

  const retakeAllPhotos = () => {
    capturedImages.forEach(url => URL.revokeObjectURL(url));
    setCapturedImages([]);
    setSelectedImages([]);
    showSnackbar('All photos removed. Camera is still active for new photos.', 'info');
  };

  // Helper functions for status
  const getStatusFromApi = (apiStatus) => {
    const statusMap = {
      'upcoming': 'upcoming',
      'ongoing': 'participating',
      'completed': 'completed'
    };
    return statusMap[apiStatus] || 'upcoming';
  };

  const getSpocStatusFromApi = (enrollment, spoc_status) => {
    console.log('Getting status from enrollment:', enrollment, 'spoc_status:', spoc_status);

    // Use spoc_status from completed API if available
    if (spoc_status) {
      return spoc_status === 'participated' ? 'accepted' : '';
    }

    if (!enrollment || enrollment.terms_accepted === null || enrollment.terms_accepted === undefined) {
      return "";
    }

    const termsAccepted = enrollment.terms_accepted === "1";
    const staffApproved = enrollment.is_staff_approval === "1";

    console.log('Status check - termsAccepted:', termsAccepted, 'staffApproved:', staffApproved);

    if (termsAccepted && staffApproved) {
      return "accepted";
    } else if (termsAccepted && !staffApproved) {
      return "pending";
    } else {
      return "";
    }
  };

  const checkPhotoUploadRequirement = (talk) => {
    if (talk.status === 'participating' && talk.spocStatus === 'accepted' && !talk.eventPhotoUrl) {
      const eventDateTime = new Date(`${talk.eventDate}T${talk.eventStartTime}`);
      const currentTime = new Date();
      const timeDifference = currentTime - eventDateTime;
      const hoursDifference = timeDifference / (1000 * 60 * 60);

      if (hoursDifference >= 0 && hoursDifference <= 110) {
        setSelectedTalk(talk);
        setShowPhotoAlertDialog(true);
        return true;
      }
    }
    return false;
  };

  // Fetch data based on active tab
  const fetchDataForActiveTab = () => {
    switch (activeTab) {
      case 0:
        return fetchUpcomingTalks();
      case 1:
        return fetchParticipatingTalks();
      case 2:
        return fetchParticipatedTalks();
      case 3:
        return fetchCompletedTalks();
      default:
        return fetchUpcomingTalks();
    }
  };

  // Fetch all data on component mount
  useEffect(() => {
    if (accessToken) {
      fetchAllTalks();
    }
  }, [accessToken]);

  // Fetch data when tab changes
  useEffect(() => {
    if (accessToken) {
      fetchDataForActiveTab();
    }
  }, [activeTab, accessToken]);

  // Clean up camera and URLs on unmount
  useEffect(() => {
    return () => {
      stopCamera();
      capturedImages.forEach(url => URL.revokeObjectURL(url));
    };
  }, [capturedImages]);

  // Camera initialization
  useEffect(() => {
    if (showUploadPhotosDialog) {
      const timer = setTimeout(() => {
        if (selectedImages.length === 0) {
          initializeCamera();
        }
      }, 500);

      return () => {
        clearTimeout(timer);
        stopCamera();
      };
    }
  }, [showUploadPhotosDialog, selectedImages.length]);

  // Check for photo upload requirements when participating tab is active
  useEffect(() => {
    if (activeTab === 1) {
      const participatingTalks = careerTalks.participating || [];
      participatingTalks.forEach(talk => {
        if (talk.spocStatus === 'accepted' && !talk.eventPhotoUrl) {
          const eventDateTime = new Date(`${talk.eventDate}T${talk.eventStartTime}`);
          const currentTime = new Date();
          const timeDifference = currentTime - eventDateTime;
          const hoursDifference = timeDifference / (1000 * 60 * 60);

          if (hoursDifference >= 0 && hoursDifference <= 110) {
            setTimeout(() => {
              setSelectedTalk(talk);
              setShowPhotoAlertDialog(true);
            }, 1000);
            return;
          }
        }
      });
    }
  }, [activeTab, careerTalks.participating]);

  // Updated tabs structure
  const tabs = [
    { id: 'upcoming', label: 'Upcoming' },
    { id: 'participating', label: 'Participating' },
    { id: 'participated', label: 'Participated' },
    { id: 'completed', label: 'Completed' }
  ];

  const getTalksForCurrentTab = () => {
    switch (activeTab) {
      case 0: return careerTalks.upcoming;
      case 1: return careerTalks.participating;
      case 2: return careerTalks.participated;
      case 3: return careerTalks.completed;
      default: return [];
    }
  };

  const filteredTalks = filterTalksByDate(getTalksForCurrentTab());
  const paginatedTalks = getPaginatedTalks(filteredTalks);

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatTime = (timeString) => {
    try {
      return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return timeString;
    }
  };

  const formatTimeRange = (startTime, endTime) => {
    return `${formatTime(startTime)} - ${formatTime(endTime)}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'participating': return 'primary';
      case 'completed': return 'success';
      case 'upcoming': return 'warning';
      default: return 'default';
    }
  };

  const getSpocStatusColor = (status) => {
    switch (status) {
      case 'accepted': return 'success';
      case 'pending': return 'warning';
      case '': return 'default';
      default: return 'default';
    }
  };

  const getSpocStatusText = (status) => {
    switch (status) {
      case 'accepted': return 'Approved';
      case 'pending': return 'Pending Approval';
      case '': return '';
      default: return '';
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleShowInterest = (talk) => {
    if (!talk.showInterestedButton) {
      showSnackbar('Interest registration is closed for this talk', 'info');
      return;
    }

    if (talk.spocStatus === 'accepted' || talk.spocStatus === 'pending') {
      showSnackbar('You have already shown interest for this talk', 'info');
      return;
    }

    setSelectedTalk(talk);
    setShowTermsDialog(true);
  };

  const handleAcceptTerms = async () => {
    try {
      await acceptTermsAndConditions(selectedTalk.id);
      setShowTermsDialog(false);
      setSelectedTalk(null);
    } catch (error) {
      // Error handled in the function
    }
  };

  const handleUploadPhotos = (talk) => {
    if (!talk.enrollmentId) {
      showSnackbar('Enrollment ID not found. Please contact support.', 'error');
      return;
    }

    setSelectedTalk(talk);
    setShowUploadPhotosDialog(true);
    setStudentCount('');
    setCapturedImages([]);
    setSelectedImages([]);
  };

  const handleDownloadCertificate = (talk) => {
    if (!talk.enrollmentId) {
      showSnackbar('Enrollment ID not found', 'error');
      return;
    }
    setSelectedTalk(talk);
    setShowCertificateDialog(true);
  };

  const handleShowDetails = (talk) => {
    // Empty function - nothing will show when clicking view details
  };

  const handleViewUploadedPhotos = async (talk) => {
    if (!talk.eventPhotoUrl) {
      showSnackbar('No photos available for this event', 'info');
      return;
    }

    try {
      setLoadingState('viewPhotos', true);

      // Open dialog immediately to show loading state
      setSelectedTalk(talk);
      setShowDetailsDialog(true);

      // Parse the event photo URLs (they might be stored as JSON string or array)
      let photoKeys = [];
      try {
        if (typeof talk.eventPhotoUrl === 'string') {
          photoKeys = JSON.parse(talk.eventPhotoUrl);
        } else if (Array.isArray(talk.eventPhotoUrl)) {
          photoKeys = talk.eventPhotoUrl;
        }
      } catch (error) {
        console.error('Error parsing event photo URLs:', error);
        photoKeys = [];
      }

      // Fetch all event photos from S3
      const photoPromises = photoKeys.map(async (fileKey) => {
        try {
          const url = await fetchImageFromS3(fileKey);
          return {
            originalKey: fileKey,
            url: url || fileKey,
            fetched: url !== null
          };
        } catch (error) {
          console.error(`Error fetching photo for key ${fileKey}:`, error);
          return {
            originalKey: fileKey,
            url: fileKey,
            fetched: false
          };
        }
      });

      const fetchedPhotos = await Promise.all(photoPromises);

      // Update the talk with fetched photo URLs
      const updatedTalk = {
        ...talk,
        fetchedEventPhotos: fetchedPhotos
      };

      setSelectedTalk(updatedTalk);

    } catch (error) {
      console.error('Error loading uploaded photos:', error);
      showSnackbar('Failed to load photos', 'error');
    } finally {
      setLoadingState('viewPhotos', false);
    }
  };

  const handleCloseUploadDialog = () => {
    stopCamera();
    capturedImages.forEach(url => URL.revokeObjectURL(url));
    setShowUploadPhotosDialog(false);
    setCapturedImages([]);
    setSelectedImages([]);
    setStudentCount('');
    setSocialMediaLinks([""]);
  };

  // Get loading state for current tab
  const getCurrentTabLoading = () => {
    switch (activeTab) {
      case 0: return loading.upcoming;
      case 1: return loading.participating;
      case 2: return loading.participated;
      case 3: return loading.completed;
      default: return false;
    }
  };

  // Get filter display text
  const getFilterDisplayText = () => {
    if (!selectedMonth && !selectedYear) {
      return 'Filter by Date';
    }

    const monthText = selectedMonth ? monthOptions.find(m => m.value === selectedMonth)?.label : 'All Months';
    const yearText = selectedYear ? selectedYear : 'All Years';

    return `Filter: ${monthText} ${yearText}`;
  };

  // Show authentication warning if not logged in
  if (!accessToken) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="error">
          <Typography variant="h6" gutterBottom>
            Authentication Required
          </Typography>
          <Typography variant="body2">
            Please log in to access Career Talks.
          </Typography>
        </Alert>
      </Box>
    );
  }

  // Mobile Card View
  const MobileCard = ({ talk }) => (
    <Card sx={{ mb: 2, p: 1.5 }}>
      <CardContent sx={{ p: 1 }}>
        <Grid container spacing={1.5}>
          <Grid item xs={4}>
            <Box
              sx={{
                width: '100%',
                height: 80,
                borderRadius: 1,
                overflow: 'hidden',
                cursor: 'pointer',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                '&:hover .poster-overlay': {
                  opacity: 1,
                },
                '&:hover .poster-image': {
                  transform: 'scale(1.05)',
                }
              }}
              onClick={() => handleFetchAndShowPoster(talk)}
            >
              {loading.posterLoading[talk.id] ? (
                <Box sx={{ textAlign: 'center' }}>
                  <CircularProgress size={24} sx={{ mb: 0.5 }} />
                  <Typography variant="caption" sx={{ color: 'primary.main' }}>
                    Loading...
                  </Typography>
                </Box>
              ) : (
                <>
                  <img
                    src={careerTalkPosterImage}
                    alt="Career Talk Poster"
                    className="poster-image"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease'
                    }}
                  />
                  <Box
                    className="poster-overlay"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(25, 118, 210, 0.85)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0,
                      transition: 'opacity 0.3s ease',
                      color: 'white',
                      p: 1
                    }}
                  >
                    <ZoomIn sx={{ fontSize: 20, mb: 0.5 }} />
                    <Typography variant="caption" sx={{ fontWeight: 'medium', textAlign: 'center' }}>
                      Click to view poster
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '10px', opacity: 0.9, textAlign: 'center', mt: 0.5 }}>
                      {talk.title}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </Grid>

          <Grid item xs={8}>
            <Typography variant="subtitle2" fontWeight="600" gutterBottom>
              {talk.title}
            </Typography>

            {/* Status chips for all tabs except participating and participated */}
            {activeTab !== 1 && activeTab !== 2 && (
              <Box sx={{ mb: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                <Chip
                  label={talk.status}
                  color={getStatusColor(talk.status)}
                  size="small"
                />
                {talk.spocStatus && (
                  <Chip
                    label={getSpocStatusText(talk.spocStatus)}
                    color={getSpocStatusColor(talk.spocStatus)}
                    size="small"
                  />
                )}
              </Box>
            )}

            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" display="block" sx={{ display: 'flex', alignItems: 'center' }}>
                <CalendarToday sx={{ fontSize: 12, mr: 0.5 }} />
                {formatDate(talk.date)}
              </Typography>
              <Typography variant="caption" display="block" sx={{ display: 'flex', alignItems: 'center' }}>
                <Schedule sx={{ fontSize: 12, mr: 0.5 }} />
                {formatTimeRange(talk.startTime, talk.endTime)}
              </Typography>
            </Box>

            {/* Event Link - Disabled until event morning */}
            <Box sx={{ mb: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<LinkIcon />}
                onClick={() => window.open(talk.link, '_blank')}
                sx={{ minWidth: 'auto', px: 1 }}
                disabled={!isEventLinkVisible(talk)}
                title={!isEventLinkVisible(talk) ? "Event link will be active on the day of the event" : "Join Event"}
              >
                {isEventLinkVisible(talk) ? 'Join Event' : 'Link Disabled'}
              </Button>
            </Box>

            {/* Photo upload reminder for participating events */}
            {talk.status === 'participating' && talk.spocStatus === 'accepted' && !talk.eventPhotoUrl && (
              <Alert severity="warning" sx={{ py: 0, my: 1 }}>
                <Typography variant="caption">
                  Upload photos within 3 hours to get certificate
                </Typography>
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5, flexWrap: 'wrap' }}>
              {loading.enroll || loading.complete ? (
                <CircularProgress size={20} />
              ) : (
                <>
                  {talk.status === 'upcoming' && (
                    <Tooltip
                      title={
                        !talk.showInterestedButton
                          ? "Interest registration is closed"
                          : talk.spocStatus !== ''
                            ? "You have already shown interest"
                            : "Show Interest"
                      }
                    >
                      <span>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<ThumbUp />}
                          onClick={() => handleShowInterest(talk)}
                          color="success"
                          sx={{ minWidth: 'auto', px: 1 }}
                          disabled={talk.spocStatus !== '' || !talk.showInterestedButton}
                        >
                          Interest
                        </Button>
                      </span>
                    </Tooltip>
                  )}

                  {/* Participating: Upload Photos, View Uploaded Photos, and Download Certificate */}
                  {talk.status === 'participating' && talk.spocStatus === 'accepted' && (
                    <>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<CameraAlt />}
                        onClick={() => {
                          if (checkPhotoUploadRequirement(talk)) {
                            return;
                          }
                          handleUploadPhotos(talk);
                        }}
                        color="primary"
                        sx={{ minWidth: 'auto', px: 1 }}
                      >
                        {talk.eventPhotoUrl ? 'View Photos' : 'Upload Photos'}
                      </Button>
                      {talk.eventPhotoUrl && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<CameraAlt />}
                          onClick={() => handleViewUploadedPhotos(talk)}
                          color="info"
                          sx={{ minWidth: 'auto', px: 1 }}
                        >
                          View Uploaded
                        </Button>
                      )}
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Download />}
                        onClick={() => handleDownloadCertificate(talk)}
                        color="secondary"
                        sx={{ minWidth: 'auto', px: 1 }}
                        disabled={!talk.isCertificateIssued}
                      >
                        Certificate
                      </Button>
                    </>
                  )}

                  {/* Participated: View Uploaded Photos and Download Certificate */}
                  {talk.status === 'participated' && (
                    <>
                      {talk.eventPhotoUrl && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<CameraAlt />}
                          onClick={() => handleViewUploadedPhotos(talk)}
                          color="info"
                          sx={{ minWidth: 'auto', px: 1 }}
                        >
                          View Photos
                        </Button>
                      )}
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Download />}
                        onClick={() => handleDownloadCertificate(talk)}
                        color="secondary"
                        sx={{ minWidth: 'auto', px: 1 }}
                        disabled={!talk.isCertificateIssued}
                      >
                        Certificate
                      </Button>
                    </>
                  )}

                  {/* Completed: Download Certificate (only show if participated) */}
                  {talk.status === 'completed' && talk.hasParticipated && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Download />}
                      onClick={() => handleDownloadCertificate(talk)}
                      color="secondary"
                      sx={{ minWidth: 'auto', px: 1 }}
                      disabled={!talk.isCertificateIssued}
                    >
                      Certificate
                    </Button>
                  )}

                  {/* Completed: Show participation status */}
                  {talk.status === 'completed' && (
                    <Chip
                      label={talk.hasParticipated ? 'Participated' : 'Not Participated'}
                      color={talk.hasParticipated ? 'success' : 'default'}
                      size="small"
                    />
                  )}
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 2 }}>
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Hidden file input for photo upload fallback */}
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileSelect}
        multiple
      />

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="inherit"
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              color: 'text.secondary',
            },
            '& .Mui-selected': {
              color: 'primary.main !important',
            },
          }}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.id}
              label={tab.label}
              sx={{
                py: 1,
                minHeight: 'auto',
              }}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Date Filter with Popover */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          startIcon={<CalendarMonth />}
          onClick={handleCalendarOpen}
          size="small"
        >
          {getFilterDisplayText()}
        </Button>

        {(selectedMonth || selectedYear) && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={clearDateFilter}
            size="small"
          >
            Clear Filter
          </Button>
        )}

        {/* Filter Popover */}
        <Popover
          open={calendarOpen}
          anchorEl={calendarAnchorEl}
          onClose={handleCalendarClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <Box sx={{ p: 2, minWidth: 200 }}>
            <Typography variant="subtitle2" gutterBottom>
              Filter by Date
            </Typography>

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Month</InputLabel>
              <Select
                value={selectedMonth}
                label="Month"
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                {monthOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                label="Year"
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                {yearOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={handleCalendarClose}
                size="small"
                fullWidth
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Popover>
      </Box>

      {/* Loading state for current tab */}
      {getCurrentTabLoading() && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Content */}
      {!getCurrentTabLoading() && (
        isMobile ? (
          // Mobile View
          <Box>
            {paginatedTalks.map((talk) => (
              <MobileCard key={talk.id} talk={talk} />
            ))}

            {filteredTalks.length === 0 && (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <CalendarToday sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body1" gutterBottom>
                  No {tabs[activeTab].label.toLowerCase()} talks
                </Typography>
                {(selectedMonth || selectedYear) && (
                  <Typography variant="body2" color="text.secondary">
                    for selected date filter
                  </Typography>
                )}
              </Paper>
            )}

            {/* Pagination for Mobile */}
            {filteredTalks.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
                <Pagination
                  count={Math.ceil(pagination.totalCount / pagination.rowsPerPage)}
                  page={pagination.page}
                  onChange={handlePageChange}
                  color="primary"
                  size="small"
                />
              </Box>
            )}
          </Box>
        ) : (
          // Desktop View
          <>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 120 }}>Poster</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Event Link</TableCell>
                    {/* Status column for all tabs except participating and participated */}
                    {(activeTab !== 1 && activeTab !== 2) && <TableCell>Status</TableCell>}
                    {activeTab === 1 && <TableCell>Upload Photos</TableCell>}
                    <TableCell sx={{ width: 120 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedTalks.map((talk) => (
                    <TableRow key={talk.id} hover>
                      {/* Poster Column with Custom Image */}
                      <TableCell>
                        <Box
                          sx={{
                            position: 'relative',
                            cursor: 'pointer',
                            '&:hover .poster-overlay': {
                              opacity: 1,
                            },
                            '&:hover .poster-image': {
                              transform: 'scale(1.05)',
                            }
                          }}
                          onClick={() => handleFetchAndShowPoster(talk)}
                        >
                          {loading.posterLoading[talk.id] ? (
                            <Box
                              sx={{
                                width: 100,
                                height: 60,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#f5f5f5',
                                borderRadius: 1,
                                border: '1px solid #e0e0e0'
                              }}
                            >
                              <CircularProgress size={20} sx={{ mb: 0.5 }} />
                              <Typography variant="caption" sx={{ color: 'primary.main', fontSize: '10px' }}>
                                Loading...
                              </Typography>
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                width: 100,
                                height: 60,
                                borderRadius: 1,
                                overflow: 'hidden',
                                position: 'relative',
                                border: '1px solid #e0e0e0'
                              }}
                            >
                              <img
                                src={careerTalkPosterImage}
                                alt="Career Talk Poster"
                                className="poster-image"
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  transition: 'transform 0.3s ease'
                                }}
                              />
                              <Box
                                className="poster-overlay"
                                sx={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  backgroundColor: 'rgba(25, 118, 210, 0.85)',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  opacity: 0,
                                  transition: 'opacity 0.3s ease',
                                  color: 'white',
                                  p: 0.5
                                }}
                              >
                                <ZoomIn sx={{ fontSize: 16, mb: 0.25 }} />
                                <Typography variant="caption" sx={{ fontSize: '9px', fontWeight: 'medium', textAlign: 'center' }}>
                                  View Poster
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          <Tooltip title="Click to view event poster" arrow>
                            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 0.5, color: 'text.secondary' }}>
                              View Poster
                            </Typography>
                          </Tooltip>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(talk.date)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {formatTimeRange(talk.startTime, talk.endTime)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => window.open(talk.link, '_blank')}
                          color={isEventLinkVisible(talk) ? "primary" : "default"}
                          title={isEventLinkVisible(talk) ? "Join Meeting" : "Event link will be active on the day of the event"}
                          disabled={!isEventLinkVisible(talk)}
                        >
                          <LinkIcon fontSize="small" />
                        </IconButton>
                      </TableCell>

                      {/* Status column for Upcoming tab - show full status */}
                      {activeTab === 0 && (
                        <TableCell>
                          {talk.spocStatus && (
                            <Chip
                              label={getSpocStatusText(talk.spocStatus)}
                              color={getSpocStatusColor(talk.spocStatus)}
                              size="small"
                            />
                          )}
                        </TableCell>
                      )}

                      {/* Status column for Completed tab - Only show participated/not participated */}
                      {activeTab === 3 && (
                        <TableCell>
                          <Chip
                            label={talk.hasParticipated ? 'Participated' : 'Not Participated'}
                            color={talk.hasParticipated ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      )}

                      {/* Upload Photos column for Participating tab */}
                      {activeTab === 1 && (
                        <TableCell>
                          {talk.spocStatus === 'accepted' && (
                            <Button
                              size="small"
                              startIcon={<CameraAlt />}
                              onClick={() => {
                                if (checkPhotoUploadRequirement(talk)) {
                                  return;
                                }
                                handleUploadPhotos(talk);
                              }}
                              variant={talk.eventPhotoUrl ? "outlined" : "contained"}
                              color="primary"
                            >
                              {talk.eventPhotoUrl ? 'View' : 'Upload'}
                            </Button>
                          )}
                        </TableCell>
                      )}

                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {loading.enroll || loading.complete ? (
                            <CircularProgress size={20} />
                          ) : (
                            <>
                              {/* Upcoming tab actions - Interested button (inactive when not empty) */}
                              {activeTab === 0 && (
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => handleShowInterest(talk)}
                                  color="info"
                                  sx={{ minWidth: 'auto', px: 1 }}
                                  disabled={talk.spocStatus !== '' || !talk.showInterestedButton}
                                >
                                  Interested
                                </Button>
                              )}

                              {/* Participating tab actions - View Uploaded Photos and Download Certificate */}
                              {activeTab === 1 && talk.spocStatus === 'accepted' && (
                                <>
                                  {talk.eventPhotoUrl && (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleViewUploadedPhotos(talk)}
                                      color="info"
                                      title="View Uploaded Photos"
                                    >
                                      <CameraAlt fontSize="small" />
                                    </IconButton>
                                  )}
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDownloadCertificate(talk)}
                                    color="secondary"
                                    title="Download Certificate"
                                    disabled={!talk.isCertificateIssued}
                                  >
                                    <Download fontSize="small" />
                                  </IconButton>
                                </>
                              )}

                              {/* Participated tab actions - View Uploaded Photos and Download Certificate */}
                              {activeTab === 2 && (
                                <>
                                  {talk.eventPhotoUrl && (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleViewUploadedPhotos(talk)}
                                      color="info"
                                      title="View Uploaded Photos"
                                    >
                                      <CameraAlt fontSize="small" />
                                    </IconButton>
                                  )}
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDownloadCertificate(talk)}
                                    color="secondary"
                                    title="Download Certificate"
                                    disabled={!talk.isCertificateIssued}
                                  >
                                    <Download fontSize="small" />
                                  </IconButton>
                                </>
                              )}
                              {/* Completed tab actions - Download Certificate (only show if participated) */}
                              {activeTab === 3 && talk.hasParticipated && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleDownloadCertificate(talk)}
                                  color="secondary"
                                  title="Download Certificate"
                                  disabled={!talk.isCertificateIssued}
                                >
                                  <Download fontSize="medium" />
                                </IconButton>
                              )}
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {filteredTalks.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <CalendarToday sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body1">
                    No {tabs[activeTab].label.toLowerCase()} talks
                  </Typography>
                  {(selectedMonth || selectedYear) && (
                    <Typography variant="body2" color="text.secondary">
                      for selected date filter
                    </Typography>
                  )}
                </Box>
              )}
            </TableContainer>

            {/* Pagination for Desktop */}
            {filteredTalks.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
                <Pagination
                  count={Math.ceil(pagination.totalCount / pagination.rowsPerPage)}
                  page={pagination.page}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )
      )}

      {/* Poster View Dialog */}
      <Dialog
        open={showPosterDialog}
        onClose={() => setShowPosterDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">
            Event Poster - {selectedTalk?.title}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}>
          {selectedTalk && selectedTalk.posterUrl ? (
            <img
              src={selectedTalk.posterUrl}
              alt={selectedTalk.title}
              style={{
                maxWidth: '100%',
                maxHeight: 400,
                objectFit: 'contain',
                borderRadius: 8
              }}
              onError={(e) => {
                e.target.src = 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=400&h=300&fit=crop';
              }}
            />
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <ImageIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Poster image not available
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPosterDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Uploaded Photos View Dialog - WITH PROMINENT LOADING STATE */}
      <Dialog
        open={showDetailsDialog}
        onClose={() => setShowDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">
            Uploaded Photos - {selectedTalk?.title}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {loading.viewPhotos ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 300,
                gap: 2
              }}
            >
              <CircularProgress size={48} />
              <Typography variant="body1" color="text.secondary">
                Loading photos...
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Please wait while we fetch the event photos
              </Typography>
            </Box>
          ) : (
            selectedTalk && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Students Attended: {selectedTalk.studentsAttended || 'Not specified'}
                </Typography>

                {selectedTalk.fetchedEventPhotos && selectedTalk.fetchedEventPhotos.length > 0 ? (
                  <Grid container spacing={2}>
                    {selectedTalk.fetchedEventPhotos.map((photo, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Box sx={{ position: 'relative' }}>
                          <img
                            src={photo.url}
                            alt={`Event photo ${index + 1}`}
                            style={{
                              width: '100%',
                              height: 200,
                              objectFit: 'cover',
                              borderRadius: 4,
                              border: '2px solid #e0e0e0'
                            }}
                            onError={(e) => {
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23f3f4f6" width="400" height="300"/%3E%3Ctext x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="14" fill="%239ca3af"%3EImage not available%3C/text%3E%3C/svg%3E';
                            }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 4,
                              left: 4,
                              backgroundColor: 'rgba(0,0,0,0.7)',
                              color: 'white',
                              padding: '2px 6px',
                              borderRadius: 1,
                              fontSize: '10px'
                            }}
                          >
                            Photo {index + 1}
                          </Box>
                          {!photo.fetched && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                backgroundColor: 'rgba(255,152,0,0.7)',
                                color: 'white',
                                padding: '2px 6px',
                                borderRadius: 1,
                                fontSize: '8px'
                              }}
                            >
                              Direct URL
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Alert severity="info">
                    <Typography variant="body2">
                      No photos available for this event
                    </Typography>
                  </Alert>
                )}
              </Box>
            )
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setShowDetailsDialog(false)}
            size="small"
            disabled={loading.viewPhotos}
          >
            Close
          </Button>

          {!loading.viewPhotos && selectedTalk && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<CameraAlt />}
              onClick={() => {
                setShowDetailsDialog(false);
                handleUploadPhotos(selectedTalk);
              }}
            >
              Reupload Photos
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Terms & Conditions Dialog */}
      <Dialog
        open={showTermsDialog}
        onClose={() => setShowTermsDialog(false)}
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6">
            Terms & Conditions
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ py: 1 }}>
          <Typography variant="body2" >

            * Hall with a minimum capacity of 200 students

          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            * Laptop

          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            * Projector and screen

          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            * Internet connection (Wi-Fi or Ethernet)
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            * Sound system with microphone
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setShowTermsDialog(false)}
            size="small"
            disabled={loading.enroll}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAcceptTerms}
            variant="contained"
            color="success"
            size="small"
            startIcon={loading.enroll ? <CircularProgress size={16} /> : <ThumbUp />}
            disabled={loading.enroll}
          >
            {loading.enroll ? 'Accepting...' : 'Accept'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Photos Dialog */}
      <Dialog
        open={showUploadPhotosDialog}
        onClose={handleCloseUploadDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6">
            Upload Event Photos - {selectedTalk?.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Maximum 4 photos allowed {selectedImages.length > 0 && `(${selectedImages.length}/4)`}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Number of Students Attended"
              type="number"
              size="small"
              value={studentCount}
              onChange={(e) => setStudentCount(e.target.value)}
              fullWidth
              required
              helperText="Enter the total number of students who attended"
            />

            {/* Add Social Media Links Section */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Social Media Links
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Add links to your social media posts about this event (Facebook, Instagram, Twitter, LinkedIn, etc.)
              </Typography>

              {socialMediaLinks.map((link, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="https://www.facebook.com/your-post or https://www.instagram.com/p/your-post"
                    value={link}
                    onChange={(e) => {
                      const newLinks = [...socialMediaLinks];
                      newLinks[index] = e.target.value;
                      setSocialMediaLinks(newLinks);
                    }}
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ color: 'text.secondary', mr: 1 }}>
                          🔗
                        </Box>
                      ),
                    }}
                  />
                  {index === socialMediaLinks.length - 1 ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setSocialMediaLinks([...socialMediaLinks, ""])}
                      disabled={socialMediaLinks.length >= 5 || socialMediaLinks.some(l => l.trim() === "")}
                    >
                      Add More
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        const newLinks = socialMediaLinks.filter((_, i) => i !== index);
                        setSocialMediaLinks(newLinks);
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </Box>
              ))}

              {socialMediaLinks.length >= 5 && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Maximum 5 social media links allowed
                </Alert>
              )}
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Event Photos {selectedImages.length === 0 && '(Required)'}
              </Typography>

              {/* Camera Preview */}
              {true && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                  <Box
                    sx={{
                      width: '100%',
                      height: 300,
                      border: '2px solid #1976d2',
                      borderRadius: 1,
                      overflow: 'hidden',
                      backgroundColor: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}
                  >
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                  </Box>

                  {/* Camera buttons */}
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <Button
                      variant="contained"
                      startIcon={<CameraAlt />}
                      onClick={capturePhoto}
                      size="small"
                      disabled={selectedImages.length >= 4}
                    >
                      Capture Photo ({selectedImages.length}/4)
                    </Button>

                    <Button
                      variant="outlined"
                      startIcon={<VideocamOff />}
                      onClick={stopCamera}
                      size="small"
                    >
                      Stop Camera
                    </Button>

                    <Button
                      variant="outlined"
                      startIcon={<CameraAlt />}
                      onClick={() => fileInputRef.current?.click()}
                      size="small"
                      disabled={selectedImages.length >= 4}
                    >
                      Upload From Gallery
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Display captured photos */}
              {selectedImages.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Selected Photos ({selectedImages.length}/4)
                  </Typography>
                  <Grid container spacing={2}>
                    {capturedImages.map((imageUrl, index) => (
                      <Grid item xs={12} sm={4} key={index}>
                        <Box sx={{ position: 'relative' }}>
                          <img
                            src={imageUrl}
                            alt={`Event photo ${index + 1}`}
                            style={{
                              width: '100%',
                              height: 150,
                              objectFit: 'cover',
                              borderRadius: 4,
                              border: '2px solid #e0e0e0'
                            }}
                          />
                          <IconButton
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              backgroundColor: 'rgba(0,0,0,0.5)',
                              color: 'white',
                              '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.7)',
                              }
                            }}
                            onClick={() => removePhoto(index)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 4,
                              left: 4,
                              backgroundColor: 'rgba(0,0,0,0.7)',
                              color: 'white',
                              padding: '2px 6px',
                              borderRadius: 1,
                              fontSize: '10px'
                            }}
                          >
                            Photo {index + 1}
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Photo management buttons */}
                  {selectedImages.length > 0 && (
                    <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                      {selectedImages.length < 3 && !isCameraActive && (
                        <Button
                          variant="outlined"
                          startIcon={<Videocam />}
                          onClick={initializeCamera}
                          size="small"
                        >
                          Add More Photos
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        startIcon={<Delete />}
                        onClick={retakeAllPhotos}
                        size="small"
                        color="error"
                      >
                        Remove All Photos
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            {/* Success message */}
            {selectedImages.length > 0 && (
              <Alert severity="success" sx={{ mt: 1 }}>
                <Typography variant="caption">
                  Ready to submit {selectedImages.length} photo(s)! All photos will be uploaded together.
                </Typography>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseUploadDialog}
            size="small"
            disabled={loading.complete}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitPhotos}
            variant="contained"
            size="small"
            disabled={!studentCount || selectedImages.length === 0 || loading.complete}
            startIcon={loading.complete ? <CircularProgress size={16} /> : <CameraAlt />}
          >
            {loading.complete ? 'Submitting...' : `Submit ${selectedImages.length} Photo(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      {showCertificateDialog && selectedTalk && (
        <SpocCertificateDownload
          talk={selectedTalk}
          onClose={() => {
            setShowCertificateDialog(false);
            setSelectedTalk(null);
          }}
          authToken={AUTH_TOKEN}
        />
      )}
    </Box>
  );
};

export default CareerTalk_Spoc;